// ABOUTME: Unloading methods configuration form — a two-column checkbox grid over a values: string[] of method codes.
// ABOUTME: Uses react-hook-form; toggles maintain a de-duplicated selection; read-only support mirrors RetarderForm.

import {TextMedium} from '@hm/fundamental'
import React, {useEffect, useMemo} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {UNLOADING_METHOD_CODES} from '../constants'
import {UnloadingMethodEntityOptions} from '../types'
import {UnloadingMethodsConfig, UnloadingMethodsFormValues} from '../types/unloadingMethods'
import {OverrideDot} from './OverrideDot'
import {UnloadingMethodCheckbox} from './UnloadingMethodCheckbox'

export interface UnloadingMethodsFormProps {
  config: UnloadingMethodsConfig
  isReadOnly: boolean
  isCountryDefault: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: UnloadingMethodsFormValues) => void
  /** Backend-provided allowed method codes. Falls back to the hardcoded UNLOADING_METHOD_CODES. */
  options?: UnloadingMethodEntityOptions
}

type UnloadingMethodOption = {code: string; labelKey: string}

/**
 * Resolves the displayable method list: the backend `allowedValues` filtered to only known codes
 * (those in UNLOADING_METHOD_CODES), otherwise the hardcoded UNLOADING_METHOD_CODES. Exported for
 * unit testing.
 */
export const resolveMethodOptions = (allowedValues?: string[]): UnloadingMethodOption[] => {
  if (!allowedValues || allowedValues.length === 0) return UNLOADING_METHOD_CODES
  const knownCodes = new Set(UNLOADING_METHOD_CODES.map((m) => m.code))
  return allowedValues
    .filter((code) => knownCodes.has(code))
    .map((code) => ({
      code,
      labelKey: `countryConfigurations.unloadingMethods.codes.${code}`,
    }))
}

/**
 * Returns the codes whose presence differs between the current selection and the inherited
 * (parent) selection — i.e. the symmetric difference. Pure helper, exported for unit testing.
 * Used to decide which checkboxes are "overridden at this level" once an inherited array is
 * available. Order follows the supplied method list (defaults to UNLOADING_METHOD_CODES).
 */
export const computeOverriddenCodes = (
  current: string[],
  inherited: string[],
  methods: UnloadingMethodOption[] = UNLOADING_METHOD_CODES,
): string[] => {
  const currentSet = new Set(current)
  const inheritedSet = new Set(inherited)
  return methods
    .map((m) => m.code)
    .filter((code) => currentSet.has(code) !== inheritedSet.has(code))
}

const toFormValues = (cfg: UnloadingMethodsConfig): UnloadingMethodsFormValues => ({
  values: cfg.values,
})

export const UnloadingMethodsForm: React.FC<UnloadingMethodsFormProps> = ({
  config,
  isReadOnly,
  isCountryDefault,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  options,
}) => {
  const {t} = useTranslation()
  const formDefaultValues = useMemo(() => toFormValues(config), [config])
  const {control, setValue, reset, formState: {isDirty}} = useForm<UnloadingMethodsFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  // Method list: backend allowedValues when present, else the hardcoded list. Split into two
  // columns (left gets the first half, matching the Figma layout).
  const methodOptions = useMemo(
    () => resolveMethodOptions(options?.allowedValues),
    [options]
  )
  const {leftColumnCodes, rightColumnCodes} = useMemo(() => {
    const half = Math.ceil(methodOptions.length / 2)
    return {leftColumnCodes: methodOptions.slice(0, half), rightColumnCodes: methodOptions.slice(half)}
  }, [methodOptions])

  const selected = useWatch({control, name: 'values'})
  const selectedCodes = useMemo<string[]>(() => selected ?? [], [selected])

  const values: UnloadingMethodsFormValues = useMemo(() => ({values: selectedCodes}), [selectedCodes])

  // Require at least one selected unloading method when an override exists at this level.
  const isValid = selectedCodes.length > 0

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    if (isDirty) {
      onValuesChange?.(values)
    }
  }, [values, isDirty, onValuesChange])

  const handleToggle = (code: string) => (checked: boolean) => {
    const next = checked
      ? selectedCodes.includes(code)
        ? selectedCodes
        : [...selectedCodes, code]
      : selectedCodes.filter((c) => c !== code)
    setValue('values', next, {shouldDirty: true})
  }

  // Per-checkbox override dot: a code is "set at this level" when its checked state differs from the
  // country default (parent) selection. Hidden on the country-default scope itself.
  const overriddenCodes = useMemo(
    () =>
      new Set(
        isCountryDefault
          ? []
          : computeOverriddenCodes(selectedCodes, config.parentValues, methodOptions)
      ),
    [isCountryDefault, selectedCodes, config.parentValues, methodOptions]
  )

  const renderColumn = (codes: UnloadingMethodOption[]) => (
    <div className="flex flex-col gap-3">
      {codes.map((method) => {
        const checked = selectedCodes.includes(method.code)
        return (
          <UnloadingMethodCheckbox
            key={method.code}
            code={method.code}
            label={t(method.labelKey)}
            checked={checked}
            isOverridden={overriddenCodes.has(method.code)}
            disabled={isReadOnly}
            onChange={handleToggle(method.code)}
            data-cy={`unloading-method-${method.code}`}
          />
        )
      })}
    </div>
  )

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy="unloading-methods-form">
      <div className="flex items-center gap-2">
        <OverrideDot />
        <TextMedium className="text-foreground_faded">
          {t('countryConfigurations.unloadingMethods.legendOverridden')}
        </TextMedium>
      </div>

      {!isValid && !isReadOnly && isDirty && (
        <span className="text-xs leading-4 text-warning">
          {t('countryConfigurations.unloadingMethods.errorAtLeastOne')}
        </span>
      )}

      <div className="grid grid-cols-2 gap-x-16 gap-y-3 w-full">
        {renderColumn(leftColumnCodes)}
        {renderColumn(rightColumnCodes)}
      </div>
    </div>
  )
}
